import { useCallback, useEffect, useRef, useState } from 'react'
import { useReducedMotion } from 'framer-motion'

const AUTOPLAY_MS = 6500

export function useCarousel(count: number, externallyPaused: boolean) {
  const scrollerRef = useRef<HTMLDivElement>(null)
  const slideRefs = useRef<(HTMLElement | null)[]>([])
  const activeRef = useRef(0)
  const frameRef = useRef<number | null>(null)
  const [active, setActive] = useState(0)
  const [hovering, setHovering] = useState(false)
  const [interacted, setInteracted] = useState(false)
  const reduceMotion = useReducedMotion()

  const goTo = useCallback(
    (index: number) => {
      const scroller = scrollerRef.current
      const slide = slideRefs.current[index]
      if (!scroller || !slide) return
      const left = slide.offsetLeft - (scroller.clientWidth - slide.clientWidth) / 2
      scroller.scrollTo({ left, behavior: reduceMotion ? 'auto' : 'smooth' })
    },
    [reduceMotion],
  )

  const onScroll = useCallback(() => {
    if (frameRef.current !== null) return
    frameRef.current = requestAnimationFrame(() => {
      frameRef.current = null
      const scroller = scrollerRef.current
      if (!scroller) return
      const center = scroller.scrollLeft + scroller.clientWidth / 2
      let closest = 0
      let distance = Infinity
      slideRefs.current.forEach((slide, i) => {
        if (!slide) return
        const d = Math.abs(slide.offsetLeft + slide.clientWidth / 2 - center)
        if (d < distance) {
          distance = d
          closest = i
        }
      })
      activeRef.current = closest
      setActive(closest)
    })
  }, [])

  const markInteracted = useCallback(() => setInteracted(true), [])

  const next = useCallback(() => {
    markInteracted()
    goTo((activeRef.current + 1) % count)
  }, [count, goTo, markInteracted])

  const prev = useCallback(() => {
    markInteracted()
    goTo((activeRef.current - 1 + count) % count)
  }, [count, goTo, markInteracted])

  const select = useCallback(
    (index: number) => {
      markInteracted()
      goTo(index)
    },
    [goTo, markInteracted],
  )

  useEffect(() => {
    if (reduceMotion || interacted || hovering || externallyPaused) return
    const id = window.setInterval(() => goTo((activeRef.current + 1) % count), AUTOPLAY_MS)
    return () => window.clearInterval(id)
  }, [count, goTo, reduceMotion, interacted, hovering, externallyPaused])

  useEffect(
    () => () => {
      if (frameRef.current !== null) cancelAnimationFrame(frameRef.current)
    },
    [],
  )

  return {
    scrollerRef,
    slideRefs,
    active,
    next,
    prev,
    select,
    onScroll,
    markInteracted,
    setHovering,
  }
}
